// Pure copy helpers that explain why Firefly is not in its ordinary
// (Focus / purple) state. Mirrors `impersonationBanner.ts`: unit-testable
// without React, and keeps chrome components from inlining tone→copy maps.
//
// Doctrine: whenever `deriveActivityDockStatus` returns a non-null tone,
// the expanded Activity panel must surface a short status reason so users
// can see *why* Firefly is alerting / nudging / celebrating — not only
// that the glow color changed.

import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import { resolveFireflyIntent } from "./firefly/resolveFireflyIntent";
import type { FireflyIntent } from "./firefly/fireflyIntents";

export type ActivityDockStatusReasonCode =
  | "unsent_leads"
  | "ads_goal"
  | "plant_material_request"
  | "negative_on_hand"
  | "open_qc"
  | "stale_wac"
  | "kb_gaps"
  | "ai_prompting_suggestions"
  | "pending_approvals";

/** Short mode name for each expressive intent (Focus is ordinary — omitted). */
const INTENT_MODE_LABEL: Record<Exclude<FireflyIntent, "focus">, string> = {
  critical: "Critical attention",
  guidance: "Guidance",
  teaching: "Teaching",
  discovery: "Discovery",
};

/**
 * Returns banner copy when Firefly is in a non-ordinary tone, or `null`
 * when the dock is calm (no tone / no reason).
 */
export function formatFireflyStatusReasonBanner(
  tone: KpiTone | null | undefined,
  reasonLabel: string | null | undefined,
): string | null {
  const trimmed = (reasonLabel ?? "").trim();
  if (!tone || !trimmed) return null;
  const intent = resolveFireflyIntent(tone);
  if (intent === "focus") return null;
  const mode = INTENT_MODE_LABEL[intent];
  return `${mode}: ${trimmed}`;
}
